import { useCallback } from 'react';

import {
  ResendCriticalActionMutation,
  ResendCriticalActionMutationVariables,
} from '@generated/ResendCriticalActionMutation';
import { RESEND_CRITICAL_ACTION_MUTATION } from '@graphql/mutations/ResendCriticalActionMutation';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { useTranslation } from 'react-i18next';

import { UseResendCriticalAction } from '.';

/**
 * Hook to send a user's critical action the confirmation email again.
 * @param action - the critical action
 * @param agentID - the user's id
 * @param email - the user's email
 * @returns an object holding a function responsible for sending the email and its loading state
 */
export function useResendCriticalAction(
  action: string,
  agentID: string,
  email: string
): UseResendCriticalAction {
  const { t } = useTranslation();

  const [mutate, { loading }] = useGraphMutationWithFeedback<
    ResendCriticalActionMutation,
    ResendCriticalActionMutationVariables
  >(RESEND_CRITICAL_ACTION_MUTATION, { type: 'snackbar' });

  const resendCriticalAction = useCallback(async () => {
    await mutate(
      {
        variables: {
          input: {
            action,
            agentID,
            email,
          },
        },
      },
      { successMessage: t('user_profile.confirmation_banner.email') }
    );

    return null;
  }, [action, agentID, email, mutate, t]);

  return { resendCriticalAction, loading };
}
